import { useTranslation } from 'react-i18next';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { ControlledAutoComplete } from 'components/shared/ControlledFields';
import { orderBy } from 'lodash';
import useGetWorkAgreementDetails from '../hooks/useGetWorkAgreementDetails';
import { useGetArrangements, useGetTravelCompensationType } from 'queries';
import { useEffect, useRef } from 'react';
import {
  arrangementTypesToOption,
  trvSetToOption,
  workAgreementsToOption
} from 'components/admin-portal/Tables/TravelCosts/Distances/utils';
import { ArrangementAndCompensationSectionProps } from '../types';

const ArrangementAndCompensationSection = ({
  rowData,
  control,
  setValue,
  employeeId,
  selectedArrangementType
}: ArrangementAndCompensationSectionProps) => {
  const { t } = useTranslation('common');
  const prevArrangementTypeRef = useRef(selectedArrangementType);

  const { data: arrangementTypes } = useGetTravelCompensationType();

  const { data: arrangements } = useGetArrangements(arrangementTypes ?? [], {
    enabled: !!arrangementTypes
  });

  const filteredArrangements = arrangements?.filter(
    (arrangement) =>
      selectedArrangementType?.id &&
      arrangement.compensationTypeId === selectedArrangementType.id
  );

  const { data: workAgreements } = useGetWorkAgreementDetails(employeeId);

  const processedWorkAgreements = workAgreements?.map((item) => ({
    ...item,
    nrString: String(item.nr)
  }));

  useEffect(() => {
    if (
      rowData &&
      arrangementTypes &&
      arrangements &&
      processedWorkAgreements
    ) {
      setValue(
        'waNr',
        workAgreementsToOption(rowData, processedWorkAgreements, 'waNr')
      );
      setValue(
        'arrangementTypes',
        arrangementTypesToOption(
          rowData,
          arrangementTypes,
          'compensationTypeName'
        )
      );
      setValue(
        'trvSet',
        trvSetToOption(rowData, arrangements, 'trvSetDescription')
      );
    }
  }, [rowData, arrangementTypes, arrangements, workAgreements]);

  // clear trvSetDescription when selectedArrangementType changes
  useEffect(() => {
    const prevId = prevArrangementTypeRef.current?.id;
    const newId = selectedArrangementType?.id;

    if (prevId && newId && prevId !== newId) {
      const props = [
        'trvSet',
        'postalCode',
        'city',
        'streetName',
        'houseNumber',
        'countryCode',
        'distanceKm',
        'travelExpensesNominal'
      ];
      props.forEach((prop) => setValue(prop, null));
    }

    prevArrangementTypeRef.current = selectedArrangementType;
  }, [selectedArrangementType, setValue]);

  return (
    <MultiStepForm.Section
      title={t('travelArrangements.arrangementAndCompensation')}
      validateFields={['arrangementTypes', 'trvSet']}
    >
      <ControlledAutoComplete
        name="waNr"
        control={control}
        variant="outlined"
        label={t('employment')}
        options={orderBy(processedWorkAgreements, ['nrString'], ['asc'])}
        idProperty="waId"
        optionLabelProp="nrString"
        fullWidth
      />
      <ControlledAutoComplete
        name="arrangementTypes"
        control={control}
        variant="outlined"
        label={t('travelArrangements.arrangementTypes')}
        options={orderBy(arrangementTypes, ['name'], ['asc'])}
        idProperty="id"
        optionLabelProp="name"
        fullWidth
      />
      <ControlledAutoComplete
        name="trvSet"
        control={control}
        variant="outlined"
        label={t('travelArrangements.compensationTypeName')}
        options={orderBy(filteredArrangements, ['description'], ['asc'])}
        idProperty="trvSetId"
        optionLabelProp="description"
        fullWidth
      />
    </MultiStepForm.Section>
  );
};

export default ArrangementAndCompensationSection;
